import {
  ancestorChain,
  countsFor,
  CURRENCIES,
  type HireReason,
  type Person,
  SQUAD_ROLES,
  type SquadRole,
} from '@tive/core';
import {
  Boxes,
  Building2,
  Crosshair,
  GitBranch,
  LogOut,
  MapPin,
  Network,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SQUAD_ROLE_LABELS, membershipsFor } from '../lib/squads';
import { resolveTheme } from '../lib/theme';
import { cn, departmentColor, formatDate, plural } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { SecretAmount } from './SecretAmount';
import { Avatar } from './Avatar';
import {
  Badge,
  Button,
  Combobox,
  DateInput,
  EmptyState,
  FieldGroup,
  IconButton,
  Input,
  Textarea,
} from './ui';

/**
 * The one text a reason carries, whichever kind it is.
 *
 * The two kinds keep their answer under different names, and the field is one
 * box: reading them through one accessor is what lets the toggle switch what
 * the box means without shuffling state around.
 */
function reasonText(reason: HireReason | null | undefined): string {
  if (!reason) return '';
  return reason.kind === 'backfill' ? reason.backfillFor : reason.justification;
}

export function PersonDetails() {
  const selectedId = useAppStore((s) => s.selectedId);
  const model = useAppStore((s) => s.model);
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');
  const isMoved = useAppStore((s) => (selectedId ? s.movedIds.has(selectedId) : false));
  const applyOps = useAppStore((s) => s.applyOps);
  const revealPerson = useAppStore((s) => s.revealPerson);
  const setView = useAppStore((s) => s.setView);
  const setFocus = useAppStore((s) => s.setFocus);
  const setHireUnder = useAppStore((s) => s.setHireUnder);
  const select = useAppStore((s) => s.select);

  const person = selectedId ? model?.byId.get(selectedId) : undefined;
  const [title, setTitle] = useState(person?.title ?? '');
  const [startDate, setStartDate] = useState(person?.startDate ?? '');
  const [location, setLocation] = useState(person?.location ?? '');
  const [kind, setKind] = useState<HireReason['kind']>(person?.plannedReason?.kind ?? 'net-new');
  const [why, setWhy] = useState(reasonText(person?.plannedReason));
  const [notes, setNotes] = useState(person?.notes ?? '');
  const [amount, setAmount] = useState(person?.plannedCost ? String(person.plannedCost.amount) : '');
  const [currency, setCurrency] = useState(person?.plannedCost?.currency ?? 'USD');
  const [name, setName] = useState(person?.name ?? '');

  useEffect(() => {
    setTitle(person?.title ?? '');
    setStartDate(person?.startDate ?? '');
    setLocation(person?.location ?? '');
    setKind(person?.plannedReason?.kind ?? 'net-new');
    setWhy(reasonText(person?.plannedReason));
    setNotes(person?.notes ?? '');
    setAmount(person?.plannedCost ? String(person.plannedCost.amount) : '');
    setCurrency(person?.plannedCost?.currency ?? 'USD');
    setName(person?.name ?? '');
  }, [
    person?.id,
    person?.title,
    person?.name,
    person?.location,
    person?.startDate,
    person?.plannedCost,
    person?.plannedReason,
    person?.notes,
  ]);

  /*
   * Every place the company already is. Derived here rather than selected,
   * because a selector that builds an array returns a new one every time and
   * the store re-renders on it forever.
   */
  const places = useMemo(
    () => [...new Set((model?.people ?? []).flatMap((someone) => someone.location ?? []))].sort(),
    [model],
  );

  if (!model || !person) {
    return (
      <EmptyState
        icon={<Users size={18} />}
        title="No one selected"
        hint="Click any card on the chart to see their details, edit their title, or plan a hire under them."
      />
    );
  }

  const counts = countsFor(model, person.id);
  const chain = ancestorChain(model, person.id).slice(0, -1);
  const isDeparting = model.departures.has(person.id);
  const dept = departmentColor(person.department, dark);
  const directs = (model.childIds.get(person.id) ?? []).map((id) => model.byId.get(id)).filter(Boolean);
  const titleDirty = title.trim() !== person.title && title.trim().length > 0;
  const nameDirty = person.isNew && name.trim() !== person.name && name.trim().length > 0;
  const startDateDirty = person.isNew && startDate !== (person.startDate ?? '');

  /*
   * Typed rather than parsed on every keystroke, so "120,000" and "120000" are
   * the same thing and a half-deleted number is not briefly a budget of 12.
   */
  const locationDirty = location.trim() !== (person?.location ?? '');
  const saveLocation = () =>
    void applyOps([{ op: 'setLocation', personId: person?.id ?? '', location: location.trim() || null }]);

  const notesDirty = notes.trim() !== (person?.notes ?? '');
  const saveNotes = () =>
    void applyOps([{ op: 'setNotes', personId: person?.id ?? '', notes: notes.trim() || null }]);

  /*
   * One box, two questions, and which one it is asking depends on the toggle.
   * Switching it therefore counts as a change even with the text untouched: a
   * justification reread as the name of whoever left is a different claim.
   */
  const reasonDirty =
    person?.isNew === true &&
    (kind !== (person.plannedReason?.kind ?? 'net-new') || why.trim() !== reasonText(person.plannedReason));

  const saveReason = () =>
    void applyOps([
      {
        op: 'setHireReason',
        personId: person?.id ?? '',
        reason:
          kind === 'backfill'
            ? { kind: 'backfill', backfillFor: why.trim() }
            : { kind: 'net-new', justification: why.trim() },
      },
    ]);

  const budget = Number(amount.replace(/[^0-9.]/g, ''));
  const nextCost = budget > 0 ? { amount: budget, currency } : null;
  const costDirty =
    person.isNew &&
    (nextCost?.amount !== person.plannedCost?.amount ||
      (nextCost !== null && nextCost.currency !== person.plannedCost?.currency));
  const saveCost = () => void applyOps([{ op: 'setHireCost', personId: person.id, cost: nextCost }]);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex items-start gap-3 border-b border-[var(--color-line)] px-3 py-3">
        <Avatar person={person} size={44} colors={dept} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-[var(--color-ink)]">{person.name}</p>
          <p className="text-[12px] leading-snug text-[var(--color-ink-2)]">{person.title}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {isMoved ? <Badge tone="moved">Moved</Badge> : null}
            {person.isNew ? <Badge tone="fresh">Planned role</Badge> : null}
            {isDeparting ? <Badge tone="leaving">Leaving</Badge> : null}
            {person.status === 'Accepted' ? (
              <Badge tone="brand">Starts {formatDate(person.startDate)}</Badge>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-1 px-3 py-3 text-[12px]">
        {person.email ? <Row icon={<Users size={12} />} value={person.email} /> : null}
        {person.department ? <Row icon={<Building2 size={12} />} value={person.department} /> : null}
        {/* The place itself is editable below, so this is only the country. */}
        {person.country ? <Row icon={<MapPin size={12} />} value={person.country} /> : null}
        {person.employeeNumber ? (
          <Row icon={<span className="text-[10px]">#</span>} value={person.employeeNumber} />
        ) : null}
        {!person.isNew ? (
          <Row
            icon={<span className="text-[10px]">⏱</span>}
            value={`Started ${formatDate(person.startDate)}`}
          />
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 px-3 pb-3">
        <div className="rounded-lg bg-[var(--color-surface-2)] px-2.5 py-2">
          <p className="text-[17px] leading-tight font-semibold tabular-nums text-[var(--color-ink)]">
            {counts.directReports}
          </p>
          <p className="text-[10px] tracking-wide text-[var(--color-ink-3)] uppercase">Direct reports</p>
        </div>
        <div className="rounded-lg bg-[var(--color-surface-2)] px-2.5 py-2">
          <p className="text-[17px] leading-tight font-semibold tabular-nums text-[var(--color-ink)]">
            {counts.totalReports}
          </p>
          <p className="text-[10px] tracking-wide text-[var(--color-ink-3)] uppercase">Total org</p>
        </div>
      </div>

      {chain.length > 0 ? (
        <div className="px-3 pb-3">
          <p className="pb-1.5 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
            Reports up through
          </p>
          <ol className="space-y-0.5">
            {chain.map((manager, index) => (
              <li key={manager.id}>
                <button
                  type="button"
                  onClick={() => {
                    select(manager.id);
                    revealPerson(manager.id);
                  }}
                  className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] text-[var(--color-ink-2)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
                >
                  <span style={{ paddingLeft: index * 8 }} className="text-[var(--color-ink-3)]">
                    {index === 0 ? '' : '└'}
                  </span>
                  <span className="truncate">{manager.name}</span>
                  <span className="truncate text-[10.5px] text-[var(--color-ink-3)]">{manager.title}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <TeamMemberships personId={person.id} personName={person.name} />

      <DottedLines person={person} />

      <div className="space-y-3 border-t border-[var(--color-line)] px-3 py-3">
        <p className="text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
          Plan a change
        </p>

        {person.isNew ? (
          <FieldGroup label="Name">
            <div className="flex gap-1.5">
              <Input aria-label="Name" value={name} onChange={(event) => setName(event.target.value)} />
              <Button
                aria-label="Save the name"
                variant={nameDirty ? 'primary' : 'outline'}
                disabled={!nameDirty}
                onClick={() => void applyOps([{ op: 'rename', personId: person.id, name: name.trim() }])}
              >
                Save
              </Button>
            </div>
          </FieldGroup>
        ) : null}

        <FieldGroup label="Title" hint="Shows up in the change list as a title change.">
          <div className="flex gap-1.5">
            <Input
              aria-label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && titleDirty) {
                  void applyOps([{ op: 'retitle', personId: person.id, title: title.trim() }]);
                }
              }}
            />
            <Button
              aria-label="Save the title"
              variant={titleDirty ? 'primary' : 'outline'}
              disabled={!titleDirty}
              onClick={() => void applyOps([{ op: 'retitle', personId: person.id, title: title.trim() }])}
            >
              Save
            </Button>
          </div>
        </FieldGroup>

        {/*
          For anybody, not only a planned role: where somebody sits is already in
          the snapshot, so this is an override like a title or a department. Pay
          is the one that cannot work this way, because the app never reads it.

          The list is every place the company already is, and it opens whole —
          picking the office next door should not mean deleting the old one
          first. `Combobox` says why that needs a component.
        */}
        <FieldGroup label="Location" hint="Where they sit. Blank for none.">
          <div className="flex gap-1.5">
            <Combobox
              label="Location"
              value={location}
              onChange={setLocation}
              options={places}
              className="min-w-0 flex-1"
            />
            <Button
              aria-label="Save the location"
              variant={locationDirty ? 'primary' : 'outline'}
              disabled={!locationDirty}
              onClick={saveLocation}
            >
              Save
            </Button>
          </div>
        </FieldGroup>

        {/*
          Editable afterwards, because the case for a role is the part most
          likely to be written badly in a hurry and rewritten before anybody
          reads it. Planned roles only: core refuses it for a real employee,
          who by existing has nothing left to justify.
        */}
        {person.isNew ? (
          <FieldGroup
            label="Why this role"
            hint={kind === 'backfill' ? 'Who is being replaced.' : 'Why the headcount is needed.'}
          >
            <div className="flex gap-1.5 pb-1.5">
              {(['net-new', 'backfill'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={kind === option}
                  onClick={() => setKind(option)}
                  className={cn(
                    'h-8 flex-1 rounded-lg border text-[12px] font-medium transition-colors',
                    kind === option
                      ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                      : 'border-[var(--color-line)] text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)]',
                  )}
                >
                  {option === 'net-new' ? 'Net new' : 'Backfill'}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5">
              <Input
                aria-label={kind === 'backfill' ? 'Backfilling' : 'Justification'}
                value={why}
                placeholder={kind === 'backfill' ? 'e.g. Sam Reed' : 'e.g. Platform work has no owner'}
                onChange={(event) => setWhy(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && reasonDirty) saveReason();
                }}
              />
              <Button
                aria-label="Save why this role"
                variant={reasonDirty ? 'primary' : 'outline'}
                disabled={!reasonDirty}
                onClick={saveReason}
              >
                Save
              </Button>
            </div>
          </FieldGroup>
        ) : null}

        {/*
          Only for a planned role. A real start date came from the HRIS and is a
          fact about somebody rather than a decision this app gets to make; a
          target start date is exactly the kind of thing a plan moves around.
        */}
        {person.isNew ? (
          <FieldGroup label="Target start" hint="When the role is meant to be filled.">
            <div className="flex gap-1.5">
              <DateInput
                aria-label="Target start"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
              <Button
                aria-label="Save the target start"
                variant={startDateDirty ? 'primary' : 'outline'}
                disabled={!startDateDirty}
                onClick={() =>
                  void applyOps([{ op: 'setStartDate', personId: person.id, startDate: startDate || null }])
                }
              >
                Save
              </Button>
            </div>
          </FieldGroup>
        ) : null}

        {/*
          For anybody, and deliberately not a change: a plan with twenty notes
          and nothing moved has not changed the org. It is still part of the
          scenario — exported with it, and the log says who wrote it.

          Ctrl+Enter saves, because Enter has to be a newline in a box that is
          for sentences.
        */}
        <FieldGroup label="Notes" hint="Yours, saved with the plan. Not counted as a change.">
          <Textarea
            aria-label="Notes"
            rows={3}
            value={notes}
            placeholder="e.g. Strong on Postgres, wants to mentor"
            onChange={(event) => setNotes(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && notesDirty) {
                saveNotes();
              }
            }}
          />
          <div className="flex justify-end pt-1.5">
            <Button
              aria-label="Save the notes"
              variant={notesDirty ? 'primary' : 'outline'}
              disabled={!notesDirty}
              onClick={saveNotes}
            >
              Save
            </Button>
          </div>
        </FieldGroup>

        {/*
          Editable, because a budget is the number most likely to be wrong first
          and revised twice. Only for a planned role: core refuses it for a real
          employee, because this app does not read pay from the HRIS and the only
          salary it should ever hold is one somebody typed about a role that does
          not exist yet.

          Covered until asked for, like every figure here. Empty clears it.
        */}
        {person.isNew ? (
          <FieldGroup label="Approximate salary" hint="Rough budget for the role. Blank for none.">
            <div className="flex gap-1.5">
              <Input
                aria-label="Approximate salary"
                value={amount}
                inputMode="numeric"
                placeholder="e.g. 120000"
                onChange={(event) => setAmount(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && costDirty) saveCost();
                }}
              />
              <select
                aria-label="Currency"
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                className="h-9 shrink-0 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[13px] text-[var(--color-ink)]"
              >
                {CURRENCIES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              <Button
                aria-label="Save the salary"
                variant={costDirty ? 'primary' : 'outline'}
                disabled={!costDirty}
                onClick={saveCost}
              >
                Save
              </Button>
            </div>
            {person.plannedCost ? (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-[var(--color-ink-3)]">
                Budgeted at <SecretAmount cost={person.plannedCost} />
              </p>
            ) : null}
          </FieldGroup>
        ) : null}

        <div className="flex flex-wrap gap-1.5">
          {/*
            Here whatever opened the pane. The details of a person can be reached
            from the table, from a search, or from the chart itself, and only the
            last of those leaves you looking at where they sit — so the way back
            to the tree has to be a button rather than something you learn.
          */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setView('reporting');
              revealPerson(person.id);
            }}
          >
            <Network size={12} /> View in org tree
          </Button>
          <Button size="sm" variant="outline" onClick={() => setHireUnder(person.id)}>
            <UserPlus size={12} /> Add a report
          </Button>
          <Button size="sm" variant="outline" onClick={() => setFocus(person.id)}>
            <Crosshair size={12} /> Focus team
          </Button>
          {person.isNew ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-[var(--color-moved)]"
              onClick={() => void applyOps([{ op: 'removeHire', personId: person.id }])}
            >
              <Trash2 size={12} /> Delete role
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className={cn(isDeparting ? 'text-[var(--color-ink-2)]' : 'text-[var(--color-leaving)]')}
              onClick={() =>
                void applyOps([{ op: 'setDeparture', personId: person.id, departing: !isDeparting }])
              }
            >
              <LogOut size={12} /> {isDeparting ? 'Not leaving' : 'Mark as leaving'}
            </Button>
          )}
        </div>
      </div>

      {directs.length > 0 ? (
        <div className="border-t border-[var(--color-line)] px-3 py-3">
          <p className="pb-1.5 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
            {plural(directs.length, 'direct report')}
          </p>
          <ul className="space-y-0.5">
            {directs.map((report) =>
              report ? (
                <li key={report.id}>
                  <button
                    type="button"
                    onClick={() => {
                      select(report.id);
                      revealPerson(report.id);
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] text-[var(--color-ink)]">
                        {report.name}
                      </span>
                      <span className="block truncate text-[10.5px] text-[var(--color-ink-3)]">
                        {report.title}
                      </span>
                    </span>
                    {countsFor(model, report.id).totalReports > 0 ? (
                      <Badge>{countsFor(model, report.id).totalReports}</Badge>
                    ) : null}
                  </button>
                </li>
              ) : null,
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Who else this person answers to, without it being their reporting line.
 *
 * A list rather than one, because the case this exists for is somebody with
 * two bosses. It changes nothing about where their card sits — `managerId`
 * decides that, always — so this section says what it does not do, in the
 * place somebody would otherwise wonder.
 */
function DottedLines({ person }: { person: Person }) {
  const model = useAppStore((s) => s.model);
  const applyOps = useAppStore((s) => s.applyOps);
  const select = useAppStore((s) => s.select);
  const revealPerson = useAppStore((s) => s.revealPerson);

  const dotted = person.dottedManagerIds ?? [];
  const taken = new Set([person.id, ...dotted]);
  /*
   * Everyone else, by name. A plain select rather than a search box: the list
   * is long but a native one is type-ahead searchable for free, and this is a
   * rare enough thing to do that it does not need its own palette.
   */
  const available = (model?.people ?? [])
    .filter((someone) => !taken.has(someone.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const set = (managerId: string, on: boolean) =>
    void applyOps([{ op: 'setDottedLine', personId: person.id, managerId, dotted: on }]);

  return (
    <div className="border-t border-[var(--color-line)] px-3 py-3">
      <p className="pb-1.5 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
        Also answers to
      </p>

      {dotted.length === 0 ? (
        <p className="pb-1.5 text-[11px] text-[var(--color-ink-3)]">
          Nobody. A dotted line shows on the chart without moving anyone.
        </p>
      ) : (
        <ul className="space-y-0.5 pb-1.5">
          {dotted.map((id) => {
            const manager = model?.byId.get(id);
            return (
              <li key={id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    select(id);
                    revealPerson(id);
                  }}
                  className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
                >
                  <GitBranch size={12} className="shrink-0 text-[var(--color-ink-3)]" />
                  <span className="truncate">{manager?.name ?? id}</span>
                </button>
                <IconButton
                  label={`Remove the dotted line to ${manager?.name ?? id}`}
                  className="size-7 hover:text-[var(--color-moved)]"
                  onClick={() => set(id, false)}
                >
                  <X size={12} />
                </IconButton>
              </li>
            );
          })}
        </ul>
      )}

      {available.length > 0 ? (
        <select
          aria-label="Add a dotted line"
          // Stays on the placeholder: picking somebody is an action, not a value.
          value=""
          onChange={(event) => {
            if (event.target.value) set(event.target.value, true);
          }}
          className="h-8 w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-ink-2)]"
        >
          <option value="">Add a dotted line…</option>
          {available.map((someone) => (
            <option key={someone.id} value={someone.id}>
              {someone.name} — {someone.title}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

/**
 * Which teams someone is on, and what they do there.
 *
 * Not part of "Plan a change": a team is a fact about how the company works
 * today, shared by every scenario, so editing it here takes effect everywhere
 * rather than becoming a proposed change on this plan.
 */
function TeamMemberships({ personId, personName }: { personId: string; personName: string }) {
  // Whichever directory the board is showing, so this panel and that board can
  // never disagree about who is on what.
  const squads = useAppStore((s) => (s.teamEditTarget === 'base' ? s.squads : s.planSquads));
  const planning = useAppStore((s) => s.teamEditTarget === 'scenario');
  const setSquadMember = useAppStore((s) => s.setSquadMember);
  const removeSquadMember = useAppStore((s) => s.removeSquadMember);
  const setView = useAppStore((s) => s.setView);

  const mine = membershipsFor(squads, personId);
  const mineIds = new Set(mine.map(({ squad }) => squad.id));
  const available = squads.squads.filter((squad) => !mineIds.has(squad.id));

  return (
    <div className="border-t border-[var(--color-line)] px-3 py-3">
      <p className="flex items-center gap-1.5 pb-1.5 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
        Teams
        {/* Says which of the two this edits, because one is a proposal and the
            other is a fact shared by every scenario. */}
        {planning ? (
          <Badge tone="brand">In this plan</Badge>
        ) : (
          <span className="font-normal normal-case">as they are today</span>
        )}
      </p>

      {squads.squads.length === 0 ? (
        <p className="text-[11.5px] text-[var(--color-ink-3)]">
          No teams yet.{' '}
          <button
            type="button"
            onClick={() => setView('teams')}
            className="text-[var(--color-brand)] hover:underline"
          >
            Create one in the team view.
          </button>
        </p>
      ) : null}

      {mine.length > 0 ? (
        <ul className="space-y-1">
          {mine.map(({ squad, role }) => (
            <li key={squad.id} className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setView('teams')}
                className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-0.5 text-left text-[12px] text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]"
              >
                <Boxes size={12} className="shrink-0 text-[var(--color-ink-3)]" />
                <span className="truncate">{squad.name}</span>
              </button>
              <select
                aria-label={`${personName}'s role on ${squad.name}`}
                value={role}
                onChange={(event) => void setSquadMember(squad.id, personId, event.target.value as SquadRole)}
                className="h-7 rounded-md border border-[var(--color-line)] bg-[var(--color-surface)] px-1 text-[11.5px] text-[var(--color-ink)]"
              >
                {SQUAD_ROLES.map((option) => (
                  <option key={option} value={option}>
                    {SQUAD_ROLE_LABELS[option]}
                  </option>
                ))}
              </select>
              <IconButton
                label={`Remove ${personName} from ${squad.name}`}
                className="size-7 hover:text-[var(--color-moved)]"
                onClick={() => void removeSquadMember(squad.id, personId)}
              >
                <X size={12} />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : null}

      {available.length > 0 ? (
        <select
          aria-label="Add to a team"
          // Stays on the placeholder: picking a team is an action, not a value.
          value=""
          onChange={(event) => {
            if (event.target.value) void setSquadMember(event.target.value, personId, 'member');
          }}
          className="mt-1.5 h-8 w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-ink-2)]"
        >
          <option value="">Add to a team…</option>
          {available.map((squad) => (
            <option key={squad.id} value={squad.id}>
              {squad.name}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

function Row({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-2 text-[var(--color-ink-2)]">
      <span className="flex size-4 shrink-0 items-center justify-center text-[var(--color-ink-3)]">
        {icon}
      </span>
      <span className="truncate">{value}</span>
    </div>
  );
}
